/**
 * TypeScript wrapper for WASM topology edge-building.
 *
 * Replaces the `Map<number,number>`-based TypeScript mirror in
 * `modeling/topology.ts` with a direct call into the Rust
 * `build_edges_from_topology` implementation — eliminating O(E) heap
 * allocation on the hot path between edit operations.
 */

import { build_topology_edges as wasmBuildTopologyEdges } from "./pkg/poseref_wasm.js";

/**
 * Build explicit edge arrays from face topology.
 *
 * Wire (loose) edges are NOT included — use `getWireEdgePairs` from
 * `modeling/topology.ts` separately if the topology carries wire edges.
 *
 * @param offsets  Face start indices into `corners` (length = face_count + 1).
 * @param corners  Flat vertex indices for all face loops.
 * @returns `{ edges, cornerEdges, edgeFlags }` — same shape as PolygonTopology fields.
 */
export function buildTopologyEdges(
  offsets: Uint32Array,
  corners: Uint32Array,
): { edges: Uint32Array; cornerEdges: Uint32Array; edgeFlags: Uint8Array } {
  const r = wasmBuildTopologyEdges(offsets, corners);
  const edges = new Uint32Array(r.edges);
  const cornerEdges = new Uint32Array(r.corner_edges);
  const edgeFlags = new Uint8Array(r.edge_flags);
  r.free();
  return { edges, cornerEdges, edgeFlags };
}
